import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { CURRENCY_SYMBOLS } from '../../utils/currencyUtils';

// Оставляем ModalOverlay и ModalContent без изменений


const ModalContent = styled.div`
  background: linear-gradient(145deg, #ffffff, #f5f6fa);
  border-radius: 14px;
  padding: 20px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh; /* Уменьшено для лучшего отображения */
  overflow-y: auto;
  overflow-x: hidden;
  box-shadow: 8px 8px 20px rgba(0, 0, 0, 0.1),
              -8px -8px 20px rgba(255, 255, 255, 0.8);
  transform: ${props => props.$isOpen ? 'scale(1)' : 'scale(0.95)'};
  transition: all 0.3s ease;
  position: relative;
  overscroll-behavior: contain;
  touch-action: pan-y; /* Разрешает вертикальный скролл на тач-устройствах */
  -webkit-overflow-scrolling: touch; /* Плавный скролл на iOS */
  
  /* Медиа-запросы для мобильных устройств */
  @media (max-width: 480px) {
    width: 95%;
    padding: 15px;
    max-height: 85vh;
  }
`;

// Улучшаем иконки для мобильного отображения
const IconSelector = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(60px, 1fr));
  gap: 12px;
  margin-top: 8px;
  
  @media (max-width: 480px) {
    grid-template-columns: repeat(auto-fill, minmax(50px, 1fr));
    gap: 8px;
  }
`;

const IconOption = styled.div`
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: ${props => props.$selected 
    ? 'linear-gradient(145deg, #4a6fa1, #5d82b3)' 
    : 'linear-gradient(145deg, #f0f3f9, #e6ebf5)'};
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${props => props.$selected 
    ? 'inset 2px 2px 5px rgba(0, 0, 0, 0.2)' 
    : '2px 2px 5px rgba(0, 0, 0, 0.05), -2px -2px 5px rgba(255, 255, 255, 0.8)'};
  
  &:hover {
    transform: ${props => props.$selected ? 'none' : 'translateY(-2px)'};
    box-shadow: ${props => props.$selected 
      ? 'inset 2px 2px 5px rgba(0, 0, 0, 0.2)' 
      : '3px 3px 7px rgba(0, 0, 0, 0.07), -3px -3px 7px rgba(255, 255, 255, 0.9)'};
  }
  
  svg {
    width: 32px;
    height: 32px;
    color: ${props => props.$selected ? '#ffffff' : '#4a6fa1'};
  }
  
  @media (max-width: 480px) {
    width: 50px;
    height: 50px;
    
    svg {
      width: 24px;
      height: 24px;
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #4a6fa1;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #8c96a5;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  
  &:hover {
    color: #4a6fa1;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
  
  @media (max-width: 480px) {
    margin-bottom: 10px;
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #4a6fa1;
`;

// Обычный ввод без валюты - ограничиваем максимальную длину
const Input = styled.input`
  width: 94%;
  padding: 12px;
  background: #f0f3f9;
  border: 1px solid #dde2ec;
  border-radius: 8px;
  font-size: 14px;
  color: #4a6fa1;
  
  &:focus {
    outline: none;
    border-color: #4a6fa1;
    box-shadow: 0 0 0 2px rgba(74, 111, 161, 0.2);
  }
  
  @media (max-width: 480px) {
    padding: 10px;
    font-size: 14px;
  }
`;

// Обновленная группа ввода с интегрированным селектором валюты
const CurrencyInputGroup = styled.div`
  position: relative;
  display: flex;
  width: 100%;
`;

const CurrencyInput = styled.input`
  width: 100%;
  padding: 12px;
  padding-right: 60px; /* Освобождаем место для селектора валюты */
  background: #f0f3f9;
  border: 1px solid #dde2ec;
  border-radius: 8px;
  font-size: 14px;
  color: #4a6fa1;
  
  &:focus {
    outline: none;
    border-color: #4a6fa1;
    box-shadow: 0 0 0 2px rgba(74, 111, 161, 0.2);
  }
  
  &:focus + div {
    border-color: #4a6fa1;
  }
  
  &::placeholder {
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }
`;

const CurrencySelectContainer = styled.div`
  position: absolute;
  top: 1px;
  right: 1px;
  bottom: 1px;
  width: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e6ebf5;
  border-left: 1px solid #dde2ec;
  border-radius: 0 8px 8px 0;
  overflow: hidden;
`;

const CurrencySelect = styled.select`
  width: 100%;
  height: 100%;
  padding: 0 20px 0 5px;
  border: none;
  background: transparent;
  color: #4a6fa1;
  font-weight: 500;
  cursor: pointer;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  
  &:focus {
    outline: none;
  }
`;

const SelectArrow = styled.div`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 4px solid #4a6fa1;
`;



const SaveButton = styled.button`
  width: 100%;
  padding: 14px;
  margin-top: 20px;
  background: linear-gradient(145deg, #4a6fa1, #5d82b3);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  font-size: 16px;
  cursor: pointer;
  box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.1),
              -2px -2px 5px rgba(255, 255, 255, 0.1);
  transition: all 0.2s ease;
  
  &:hover, &:focus {
    background: linear-gradient(145deg, #3d5e8a, #4a6fa1);
    transform: translateY(-2px);
    box-shadow: 3px 3px 7px rgba(0, 0, 0, 0.15),
                -3px -3px 7px rgba(255, 255, 255, 0.15);
  }
  
  &:active {
    transform: translateY(1px);
    box-shadow: inset 1px 1px 3px rgba(0, 0, 0, 0.2),
                inset -1px -1px 3px rgba(255, 255, 255, 0.2);
  }

  &:disabled {
    background: #a0b4d0;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
  overflow: hidden;
  -webkit-overflow-scrolling: touch; /* Плавный скролл на iOS */
`;

// Функция для получения даты следующего месяца в формате YYYY-MM-DD
const getNextMonthDate = () => {
  const today = new Date();
  // Устанавливаем следующий месяц
  today.setMonth(today.getMonth() + 1);
  
  // Форматируем дату в YYYY-MM-DD
  const year = today.getFullYear();
  // getMonth() возвращает месяц от 0 до 11, добавляем 1
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};


const SVG_ICONS = {
  // Иконка копилки
  piggybank: `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24"><path fill="currentColor" d="M19.83 7.5l-2.27-2.27c.07-.42.18-.81.32-1.15.1-.23.21-.46.34-.65-.18.01-.36.03-.55.03-1.95 0-3.67-1.51-3.67-3.46h-2c0 1.96-1.72 3.46-3.67 3.46-.19 0-.37-.02-.55-.03.13.2.25.42.35.65.14.34.25.73.32 1.15L6.17 7.5c-.88.88-1.17 1.17-1.17 2V18c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V9.5c0-.83-.29-1.12-1.17-2zM10 16c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z"/></svg>`,
  
  // Иконка кошелька
  wallet: `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24"><path fill="currentColor" d="M21 18v1c0 1.1-.9 2-2 2H5c-1.11 0-2-.9-2-2V5c0-1.1.89-2 2-2h14c1.1 0 2 .9 2 2v1h-9c-1.11 0-2 .9-2 2v8c0 1.1.89 2 2 2h9zm-9-2h10V8H12v8zm4-2.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/></svg>`,
  
  // Иконка денег/купюр
  money: `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24"><path fill="currentColor" d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/></svg>`,
  
  // Иконка домика (для накоплений на жильё)
  home: `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24"><path fill="currentColor" d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/></svg>`,
  
  // Иконка машины (для накоплений на авто)
  car: `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24"><path fill="currentColor" d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/></svg>`,
  
  // Иконка образования с денежным элементом
  education: `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24"><path fill="currentColor" d="M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82zM12 3L1 9l11 6 9-4.91V17h2V9L12 3zm1.09 14.5c-.35.27-.79.27-1.14 0L8.54 15l-1.04.5 3.5 2.5c.35.27.79.27 1.14 0L15.5 15.5l-1.04-.5-1.37 2.5z"/></svg>`,
  
  // Иконка пенсионных накоплений
  retirement: `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24"><path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1 1.05.82 1.87 2.65 1.87 1.96 0 2.4-.98 2.4-1.59 0-.83-.44-1.61-2.67-2.14-2.48-.6-4.18-1.62-4.18-3.67 0-1.72 1.39-2.84 3.11-3.21V4h2.67v1.95c1.86.45 2.79 1.86 2.85 3.39H14.3c-.05-1.11-.64-1.87-2.22-1.87-1.5 0-2.4.68-2.4 1.64 0 .84.65 1.39 2.67 1.91s4.18 1.39 4.18 3.91c-.01 1.83-1.38 2.83-3.12 3.16z"/></svg>`,
  
  // Иконка подарка (для накоплений на подарок)
  gift: `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24"><path fill="currentColor" d="M20 6h-2.18c.11-.31.18-.65.18-1 0-1.66-1.34-3-3-3-1.05 0-1.96.54-2.5 1.35l-.5.67-.5-.68C10.96 2.54 10.05 2 9 2 7.34 2 6 3.34 6 5c0 .35.07.69.18 1H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-5-2c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zM9 4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm11 15H4v-2h16v2zm0-5H4V8h5.08L7 10.83 8.62 12 11 8.76l1-1.36 1 1.36L15.38 12 17 10.83 14.92 8H20v6z"/></svg>`,
  
  // Иконка путешествий с элементом денег
  travel: `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24"><path fill="currentColor" d="M20 6h-3V4c0-1.11-.89-2-2-2H9c-1.11 0-2 .89-2 2v2H4c-1.11 0-2 .89-2 2v11c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zM9 4h6v2H9V4zm11 15H4v-2h16v2zm0-5H4V8h3v2h2V8h6v2h2V8h3v6z"/><circle fill="currentColor" cx="12" cy="12" r="2.5"/><path fill="currentColor" d="M12 10.5c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5z"/></svg>`,
  
  // Иконка графика роста
  growth: `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24"><path fill="currentColor" d="M3.5 18.49l6-6.01 4 4L22 6.92l-1.41-1.41-7.09 7.97-4-4L2 16.99z"/><path fill="currentColor" d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14z"/></svg>`,
  
  // Иконка копилки с монетой
  savings: `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24"><path fill="currentColor" d="M19.83 7.5l-2.27-2.27c.07-.42.18-.81.32-1.15.1-.23.21-.46.34-.65-.18.01-.36.03-.55.03-1.95 0-3.67-1.51-3.67-3.46h-2c0 1.96-1.72 3.46-3.67 3.46-.19 0-.37-.02-.55-.03.13.2.25.42.35.65.14.34.25.73.32 1.15L6.17 7.5c-.88.88-1.17 1.17-1.17 2V18c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V9.5c0-.83-.29-1.12-1.17-2zM12 16c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm0-2c-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1-.45-1-1-1z"/></svg>`,

  // Оригинальные иконки, которые мы оставляем
  icon1: `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24"><path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/></svg>`,
  icon2: `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24"><path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"/></svg>`,
  icon3: `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24"><path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm5-11H7v2h10z"/></svg>`,
};

// Обновляем массив иконок для использования в компоненте
// Отсортируем иконки таким образом, чтобы сначала шли финансовые

// Обновляем массив иконок для использования в компоненте
const GOAL_ICONS = Object.keys(SVG_ICONS);


const GoalModal = ({ isOpen, onClose, onSubmit, defaultCurrency }) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [progress, setProgress] = useState('');
  const [targetDate, setTargetDate] = useState(getNextMonthDate());
  const [selectedIcon, setSelectedIcon] = useState(null);
  const [currency, setCurrency] = useState(defaultCurrency || 'USD');
  const [progressCurrency, setProgressCurrency] = useState(defaultCurrency || 'USD');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Блокируем скролл боди когда открывается модальное окно
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  
  // Сбрасываем форму при открытии/закрытии модального окна
  useEffect(() => {
    if (isOpen) {
      setName('');
      setAmount('');
      setProgress('');
      // Устанавливаем дату следующего месяца
      setTargetDate(getNextMonthDate());
      setSelectedIcon(GOAL_ICONS[0]); // Используем первый ключ из массива
      setCurrency(defaultCurrency || 'USD');
      setProgressCurrency(defaultCurrency || 'USD');
    }
  }, [isOpen, defaultCurrency]);
  
  // Обновляем валюту при её изменении в контексте
  useEffect(() => {
    if (defaultCurrency) {
      setCurrency(defaultCurrency);
      setProgressCurrency(defaultCurrency);
    }
  }, [defaultCurrency]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim() || !amount || isNaN(parseFloat(amount)) || parseFloat(amount) <= 0 || !selectedIcon || !targetDate) {
      alert(t('fill_all_fields'));
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const goalData = {
        name: name.trim(),
        target_amount: parseFloat(amount),
        current_amount: progress ? parseFloat(progress) : 0,
        due_date: targetDate,
        icon: selectedIcon,
        currency,
        progress_currency: progressCurrency
      };
      
      const success = await onSubmit(goalData);
      
      if (success) {
        onClose();
      } else {
        alert(t('goal_add_error'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };
    const handleNameChange = (e) => {
    const value = e.target.value;
    // Ограничиваем до 50 символов
    if (value.length <= 50) {
      setName(value);
    }
  };
   return (
    <ModalOverlay $isOpen={isOpen} onClick={onClose}>
      <ModalContent $isOpen={isOpen} onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>{t('add_goal_title')}</ModalTitle>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </ModalHeader>
        
        <form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>{t('goal_name_label')}</Label>
            <Input 
              type="text" 
              value={name} 
              onChange={handleNameChange} 
              placeholder={t('goal_name_placeholder')} 
              required
              maxLength="50" // устанавливаем максимальную длину
            />
          </FormGroup>
          
          <FormGroup>
            <Label>{t('goal_amount_label')}</Label>
            <CurrencyInputGroup>
              <CurrencyInput 
                type="number" 
                value={amount} 
                onChange={(e) => setAmount(e.target.value)} 
                placeholder={t('goal_amount_placeholder')} 
                min="0.01" 
                step="0.01" 
                required 
              />
              <CurrencySelectContainer>
                <CurrencySelect
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                >
                  {Object.entries(CURRENCY_SYMBOLS).map(([code, symbol]) => (
                    <option key={code} value={code}>
                      {symbol}
                    </option>
                  ))}
                </CurrencySelect>
                <SelectArrow />
              </CurrencySelectContainer>
            </CurrencyInputGroup>
          </FormGroup>
          
          <FormGroup>
            <Label>{t('goal_progress_label')}</Label>
            <CurrencyInputGroup>
              <CurrencyInput 
                type="number" 
                value={progress} 
                onChange={(e) => setProgress(e.target.value)} 
                placeholder={t('goal_progress_placeholder')} 
                min="0" 
                step="0.01" 
                title={t('goal_progress_placeholder')} // добавляем всплывающую подсказку
              />
              <CurrencySelectContainer>
                <CurrencySelect
                  value={progressCurrency}
                  onChange={(e) => setProgressCurrency(e.target.value)}
                >
                  {Object.entries(CURRENCY_SYMBOLS).map(([code, symbol]) => (
                    <option key={code} value={code}>
                      {symbol}
                    </option>
                  ))}
                </CurrencySelect>
                <SelectArrow />
              </CurrencySelectContainer>
            </CurrencyInputGroup>
          </FormGroup>
          
          <FormGroup>
            <Label>{t('goal_date_label')}</Label>
            <Input 
              type="date" 
              value={targetDate} 
              onChange={(e) => setTargetDate(e.target.value)} 
              required 
              min={new Date().toISOString().split('T')[0]} // Не позволяем выбрать прошедшие даты
            />
          </FormGroup>
          
          <FormGroup>
            <Label>{t('icon_select_label')}</Label>
            <IconSelector>
              {GOAL_ICONS.map((iconKey) => (
                <IconOption 
                  key={iconKey}
                  $selected={selectedIcon === iconKey}
                  onClick={() => setSelectedIcon(iconKey)}
                  dangerouslySetInnerHTML={{ __html: SVG_ICONS[iconKey] }}
                />
              ))}
            </IconSelector>
          </FormGroup>
          
          <SaveButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? t('saving') : t('add_goal')}
          </SaveButton>
        </form>
      </ModalContent>
    </ModalOverlay>
  );
};


export default GoalModal;